import { formatCurrency } from '@eriko/shared-types';
import { FileText } from 'lucide-react';

interface Vendor {
  vendorId: string;
  name: string;
  openBillCount: number;
  balance: number;
}

interface Data {
  type: string;
  asOfDate: string;
  vendors: Vendor[];
  grandTotal: number;
}

function formatPercent(value: number): string {
  return value >= 10 ? `${value.toFixed(0)}%` : `${value.toFixed(1)}%`;
}

export function VendorBalanceReport({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const vendors = [...(d.vendors ?? [])].sort((a, b) => b.balance - a.balance);
  const grandTotal = Number(d.grandTotal ?? 0);
  const isEmpty = vendors.length === 0;

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <FileText className="h-4.25 w-4.25 text-neg" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">Vendor Balances</span>
        </div>
        <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
          as of {d.asOfDate}
        </span>
      </div>

      {/* Rows */}
      {isEmpty ? (
        <div className="px-4.5 py-10 text-center text-[13px] text-surface-500">
          No outstanding vendor balances.
        </div>
      ) : (
        <div className="px-4.5 py-2">
          {vendors.map((v) => {
            const pct = grandTotal > 0 ? (v.balance / grandTotal) * 100 : 0;
            const barWidth = Math.max(Math.min(pct, 100), pct > 0 ? 1.5 : 0);
            const tier: 'lead' | 'major' | 'minor' =
              pct >= 10 ? 'lead' : pct >= 5 ? 'major' : 'minor';
            const barColor =
              tier === 'lead'
                ? 'var(--color-neg)'
                : tier === 'major'
                  ? 'color-mix(in oklch, var(--color-neg) 60%, transparent)'
                  : 'color-mix(in oklch, var(--color-neg) 28%, transparent)';
            return (
              <div key={v.vendorId} className="group py-2">
                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                  <div className="flex min-w-0 items-baseline gap-2">
                    <span className="truncate text-[13px] text-surface-800">{v.name}</span>
                    <span className="shrink-0 text-[12px] text-surface-400 tabular-nums">
                      {v.openBillCount} {v.openBillCount === 1 ? 'bill' : 'bills'}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-baseline gap-2">
                    <span className="font-mono text-[13px] font-medium tabular-nums text-surface-900">
                      {formatCurrency(v.balance, 2)}
                    </span>
                    <span
                      className={`w-12 text-right font-mono text-[12px] tabular-nums ${
                        tier === 'lead'
                          ? 'text-neg'
                          : tier === 'major'
                            ? 'text-surface-500'
                            : 'text-surface-400'
                      }`}
                    >
                      {formatPercent(pct)}
                    </span>
                  </div>
                </div>
                <div className="h-1 overflow-hidden rounded-full bg-neg-surface">
                  <div
                    className="h-full rounded-full transition-[width] duration-500 ease-out"
                    style={{ width: `${barWidth}%`, backgroundColor: barColor }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Grand Total footer */}
      <div className="flex items-center border-t border-surface-200 px-4.5 py-2.5 text-sm font-semibold">
        <span className="flex-1 text-surface-900">Total</span>
        <span className="font-mono text-sm font-semibold tabular-nums text-surface-900">
          {formatCurrency(grandTotal, 2)}
        </span>
      </div>
    </div>
  );
}
